import React, { MouseEvent } from 'react';
import { Title } from 'bloomer/lib/elements/Title';
import { Field } from 'bloomer/lib/elements/Form/Field/Field';
import { Label } from 'bloomer/lib/elements/Form/Label';
import { Control } from 'bloomer/lib/elements/Form/Control';
import { Input } from 'bloomer/lib/elements/Form/Input';
import { Radio } from 'bloomer/lib/elements/Form/Radio';
import { Select } from 'bloomer/lib/elements/Form/Select';
import { ChromePicker, ColorResult } from 'react-color';
import { Button } from 'bloomer/lib/elements/Button';
import styled from 'styled-components';
import Spacer from '../elements/Spacer';
import exampleUnderline from '../assets/exampleUnderline.png';
import exampleNoUnderline from '../assets/exampleNoUnderline.png';
import { Image } from 'bloomer/lib/elements/Image';
import { Column } from 'bloomer/lib/grid/Column';
import { Columns } from 'bloomer/lib/grid/Columns';
import { Box } from 'bloomer/lib/elements/Box';

const FieldSet = styled.fieldset`
  padding: 15px;
  margin: 10px 0;
`;

const Legend = styled.legend`
  font-weight: bold;
`;

const CenteredImage = styled(Image)`
  margin: 0 auto;
`;

const CenteredText = styled.p`
  text-align: center;
`;

export interface SpecialNeedsForm {
  name: string;
  acronym: string;
  color: ColorResult;
  priority: string;
  sort: 'none' | 'bottom-list' | 'top-list' | 'bottom-room' | 'top-room';
  underline: boolean;
  playgroup: 'true' | 'false';
  [Key: string]: string | boolean | ColorResult;
}

interface PropTypes {
  handleChange: (e: React.FormEvent<HTMLElement>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleClear: (e: MouseEvent<HTMLButtonElement>) => void;
  handleColorChange: (color: ColorResult) => void;
  form: SpecialNeedsForm;
}

const AddNewSpecialNeedForm = ({ handleChange, handleClear, handleSubmit, handleColorChange, form }: PropTypes) => (
  <form onSubmit={handleSubmit}>
    <Title tag="h4">Add a new special need</Title>
    <Field>
      <Label htmlFor="name">Name</Label>
      <Control>
        <Input
          className="input"
          type="text"
          name="name"
          id="name"
          placeholder="No Dog Park"
          onChange={handleChange}
          value={form.name}
        />
      </Control>
    </Field>
    <Field>
      <Label htmlFor="acronym">Acronym</Label>
      <Control>
        <Input
          className="input"
          type="text"
          name="acronym"
          id="acronym"
          placeholder="NDP"
          onChange={handleChange}
          value={form.acronym}
        />
      </Control>
    </Field>
    <FieldSet>
      <Legend>Sort:</Legend>
      <p>
        The sort option will determine if an animal will appear at the top or bottom of the list. Use this in
        conjunction with the priority value to determine if it should appear before or after other special needs sorted
        to the same position. EX: Priority 1 sort bottom will appear below Priority 2 sort bottom.
      </p>
      <Field>
        <Control>
          <Radio
            type="radio"
            id="sort-bottom-room"
            name="sort"
            value="bottom-room"
            checked={form.sort === 'bottom-room'}
            onChange={handleChange}
          >
            <Spacer>Bottom (Of their room)</Spacer>
          </Radio>
        </Control>
      </Field>
      <Field>
        <Control>
          <Radio
            type="radio"
            id="sort-top-room"
            name="sort"
            value="top-room"
            checked={form.sort === 'top-room'}
            onChange={handleChange}
          >
            <Spacer>Top (Of their room)</Spacer>
          </Radio>
        </Control>
      </Field>
      <Field>
        <Control>
          <Radio
            type="radio"
            id="sort-bottom-list"
            name="sort"
            value="bottom-list"
            checked={form.sort === 'bottom-list'}
            onChange={handleChange}
          >
            <Spacer>Bottom (Of the list)</Spacer>
          </Radio>
        </Control>
      </Field>
      <Field>
        <Control>
          <Radio
            type="radio"
            id="sort-top-list"
            name="sort"
            value="top-list"
            checked={form.sort === 'top-list'}
            onChange={handleChange}
          >
            <Spacer>Top (Of the list)</Spacer>
          </Radio>
        </Control>
      </Field>
      <Field>
        <Control>
          <Radio
            type="radio"
            id="sort-none"
            name="sort"
            value="none"
            checked={form.sort === 'none'}
            onChange={handleChange}
          >
            <Spacer>None</Spacer>
          </Radio>
        </Control>
      </Field>
      <Field>
        <Label htmlFor="priority">Priority</Label>
        <Control>
          <Select name="priority" id="priority" onChange={handleChange} value={form.priority}>
            <option value="1">1 (highest)</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
            <option value="9">9</option>
            <option value="10">10 (lowest)</option>
          </Select>
        </Control>
      </Field>
    </FieldSet>

    <FieldSet>
      <Legend>List Appearence</Legend>
      <p>
        Enabling this option will make animals who have this special need appear as a unique colour on the walk list,
        with their colour code being overwritten by the colour selected here. If disabled, the animal will appear as
        normal.
      </p>
      <Field>
        <Control>
          <Radio
            type="radio"
            id="underline"
            name="underline"
            value="true"
            checked={form.underline}
            onChange={handleChange}
          >
            <Spacer>Enable</Spacer>
          </Radio>
        </Control>
      </Field>
      <Field>
        <Control>
          <Radio
            type="radio"
            id="no-underline"
            name="underline"
            value="false"
            checked={!form.underline}
            onChange={handleChange}
          >
            <Spacer>Disable</Spacer>
          </Radio>
        </Control>
      </Field>
      <Field>
        <Label htmlFor="color">Select a Colour</Label>
        <Control>
          <ChromePicker color={form.color.hex} disableAlpha={true} onChangeComplete={handleColorChange} />
        </Control>
      </Field>
      <Columns isCentered={true}>
        <Column>
          <Box>
            <CenteredImage src={exampleUnderline} alt="A special need overwriting a colour code" isSize="128x128" />
            <CenteredText>Enabled Example</CenteredText>
          </Box>
        </Column>
        <Column>
          <Box>
            <CenteredImage src={exampleNoUnderline} alt="A special need overwriting a colour code" isSize="128x128" />
            <CenteredText>Disabled Example</CenteredText>
          </Box>
        </Column>
      </Columns>
    </FieldSet>
    <FieldSet>
      <Legend>Playgroup</Legend>
      <Field>
        <Label htmlFor="playgroup">Should an animal with this "special need" be allowed to attend playgroup?</Label>
        <Control>
          <Select type="radio" id="playgroup" name="playgroup" value={form.playgroup} onChange={handleChange}>
            <option value="true">Allowed</option>
            <option value="false">Not Allowed</option>
          </Select>
        </Control>
      </Field>
    </FieldSet>
    <Field isGrouped={true}>
      <Control>
        <Button type="submit" isColor="primary">
          Submit
        </Button>
      </Control>
      <Control>
        <Button onClick={handleClear}>Cancel & Reset</Button>
      </Control>
    </Field>
  </form>
);

export default AddNewSpecialNeedForm;
